import availableBroadcastSettings from '../../constants/availableBroadcastSettings';


/**
 * Utility to generate and download stream session reports.
 */

/**
 * Standalone scoring algorithm
 */
export const calculateStreamScore = (sessionData, config = {}) => {
    const { bitrateSamples = [], logs = [], streamSettings } = sessionData;

    // Access the scoreAlgorithm config block
    const algoConfig = config.scoreAlgorithm || {};
    const threshold = algoConfig.unhealthyBitrateThreshold || 0.5;
    const sensitivity = algoConfig.scoreSensitivity || 'standard';
    const scoreSensitivityPresets = algoConfig.scoreSensitivityPresets || {};

    if (bitrateSamples.length === 0) return 100;

    const avgBitrateArr = bitrateSamples.filter(b => !isNaN(b));
    const totalSamples = avgBitrateArr.length;
    const avgBitrate = totalSamples > 0
        ? (avgBitrateArr.reduce((a, b) => a + b, 0) / totalSamples)
        : 0;

    // Use parameters from config or fall back to standard defaults if specific options are missing
    const params = scoreSensitivityPresets[sensitivity] || {
        penaltyIntensity: 2.5,
        minDropDuration: 3,
        stabilityWeight: 1.0,
        disconnectWeight: 5
    };

    // 1. Bitrate Drop Penalty (Percentage-based with Persistence)
    // We only count a drop if it lasts for at least params.persistence consecutive samples (seconds).
    // This filters out brief network fluctuations that don't affect viewer experience.
    let persistentDrops = 0;
    let currentStreak = 0;

    bitrateSamples.forEach(b => {
        if (b < threshold) {
            currentStreak++;
        } else {
            if (currentStreak >= params.minDropDuration) persistentDrops += currentStreak;
            currentStreak = 0;
        }
    });
    // Catch trial at the end
    if (currentStreak >= params.minDropDuration) persistentDrops += currentStreak;

    const dropPercentage = (persistentDrops / totalSamples) * 100;
    const bitratePenalty = Math.min(60, dropPercentage * params.penaltyIntensity);

    // 2. Stability Penalty (Normalized Coefficient of Variation)
    let stabilityPenalty = 0;
    if (totalSamples > 10) {
        const mean = avgBitrate;
        const squareDiffs = avgBitrateArr.map(value => Math.pow(value - mean, 2));
        const avgSquareDiff = squareDiffs.reduce((a, b) => a + b, 0) / squareDiffs.length;
        const stdDev = Math.sqrt(avgSquareDiff);

        // CV = StdDev / Mean. CV > 0.5 is very unstable.
        const cv = mean > 0 ? (stdDev / mean) : 1;
        if (cv > 0.5) stabilityPenalty = 20 * params.stabilityWeight;
        else if (cv > 0.25) stabilityPenalty = 10 * params.stabilityWeight;
        else if (cv > 0.1) stabilityPenalty = 5 * params.stabilityWeight;
    }

    // 3. Target Alignment Penalty (Cumulative Deficit)
    // We calculate how much the actual bitrate deviates from the target
    // and for how long. Any sample below 90% of target contributes to the penalty.
    let targetPenalty = 0;
    const numericTarget = streamSettings ? parseFloat(streamSettings.maxBitrate) : 0;
    if (numericTarget > 0) {
        let performanceDeficit = 0;
        const targetThreshold = numericTarget * (params.targetThresholdRatio || 0.9);

        bitrateSamples.forEach(b => {
            if (b < targetThreshold) {
                // Depth: proportional gap from target
                performanceDeficit += (numericTarget - b) / numericTarget;
            }
        });

        // Normalize by duration and apply intensity
        // A full stream at 50% target would result in a ~50 penalty.
        targetPenalty = (performanceDeficit / totalSamples) * 100 * (params.targetIntensity || 1.0);
        targetPenalty = Math.min(60, targetPenalty); // Cap penalty
    }

    // 4. Stateful Disconnect Penalty (Distinguish blips from permanent failures)
    // Wireless setups often have transient blips. We only heavily penalize "Permanent Failures"
    // (networks that disconnected and never reconnected).
    const linkStatus = {}; // provider -> 'connected' | 'disconnected'
    let blipCount = 0;

    (logs || []).forEach(l => {
        if (!l.message) return;
        const msg = l.message.toLowerCase();
        // Extract provider from common log formats: "Link 'WiFi' disconnected" or "Secondary Link disconnected"
        const provMatch = msg.match(/link ['"](.*)['"]/) || msg.match(/secondary (.*) disconnected/i);
        const provider = provMatch ? provMatch[1] : 'unknown';

        if (msg.includes('disconnected')) {
            linkStatus[provider] = 'disconnected';
        } else if (msg.includes('reconnected') || (msg.includes('connected') && !msg.includes('disconnected'))) {
            if (linkStatus[provider] === 'disconnected') {
                blipCount++;
            }
            linkStatus[provider] = 'connected';
        }
    });

    const permanentFailCount = Object.values(linkStatus).filter(status => status === 'disconnected').length;

    // Normalization and Weighting
    // Permanent failures: 10 points each (capped at 20)
    // Transient blips: Normalized by duration (1 per hour is acceptable)
    const normalizedBlipRatio = blipCount / (totalSamples / 3600 || 1);
    const blipPenalty = Math.min(2, normalizedBlipRatio * (params.disconnectWeight || 5) * 0.02); // Cap blips at 2 points, ultra-forgiving (98%)
    const permanentPenalty = Math.min(3, permanentFailCount * 1); // 1 point per dead network (capped at 3)

    const disconnectPenalty = blipPenalty + permanentPenalty;

    const finalScore = 100 - bitratePenalty - stabilityPenalty - targetPenalty - disconnectPenalty;

    return {
        score: Math.max(0, Math.round(finalScore)),
        breakdown: {
            criticalDrops: Math.round(bitratePenalty),
            stability: Math.round(stabilityPenalty),
            targetAlignment: Math.round(targetPenalty),
            disconnects: Math.round(disconnectPenalty)
        }
    };
};

/**
 * Generates automated summary and suggestions based on session data
 */
export const generateInsights = (sessionData) => {
    const {
        settingChanges = [],
        streamSettings = {},
        deviceInfo = [],
        bitrateSamples = [],
        startTime,
        exports = []
    } = sessionData;

    const summary = [];
    const suggestions = [];

    // Helper to map setting IDs to names
    const getSettingName = (id) => {
        const setting = availableBroadcastSettings.find(s => s.id === id);
        return setting ? setting.name : id;
    };

    // Helper to map setting values
    const getSettingValue = (id, val) => {
        const setting = availableBroadcastSettings.find(s => s.id === id);
        if (setting && setting.valuesArray) {
            const entry = setting.valuesArray.find(v => v.value === val);
            return entry ? entry.displayedName : val;
        }
        return val;
    };

    // 1. Generate Summary
    if (startTime) {
        summary.push(`Stream started at ${new Date(startTime).toLocaleTimeString()}.`);
    }

    if (settingChanges.length > 0) {
        settingChanges.forEach(change => {
            const name = getSettingName(change.setting);
            const from = getSettingValue(change.setting, change.from);
            const to = getSettingValue(change.setting, change.to);

            // Skip if value is an object (safety check)
            if (typeof from === 'object' || typeof to === 'object') return;

            summary.push(`${name} updated from ${from} to ${to} at ${change.time}.`);
        });
    } else {
        summary.push("Stream configuration remained stable throughout the session.");
    }

    // Hardware Summary
    const weakCount = deviceInfo.filter(d => d.signalStrength !== undefined && d.signalStrength < 25).length;
    if (weakCount > 0) {
        summary.push(`Observed poor signal strength on ${weakCount} connection links during the session.`);
    }

    const hotCount = deviceInfo.filter(d => d.temperature !== undefined && d.temperature > 45).length;
    if (hotCount > 0) {
        summary.push(`Thermal warnings detected: ${hotCount} device(s) reported high battery temperatures (>45°C).`);
    }

    // 2. Generate Suggestions

    // SRT Suggestion: Recommend H.265 if using H.264
    const hasSRT = (streamSettings.outputName && streamSettings.outputName.toLowerCase().includes('srt')) ||
        (sessionData.exports || []).some(exp => exp.isEnabled && getExportTypeName(exp.type).toLowerCase().includes('srt'));

    // Check for H.264 (be flexible with string format)
    const isH264 = streamSettings.codec && streamSettings.codec.toString().toUpperCase().includes('264');

    if (hasSRT && isH264) {
        suggestions.push({
            type: 'optimization',
            icon: 'speed',
            title: 'Protocol Optimization (SRT)',
            message: 'For SRT streams, H.265 (HEVC) codec is highly recommended to achieve better quality at lower bitrates.'
        });
    }

    // RTMP Suggestion: Avoid Live/Avg profiles for CDN
    const hasRTMP = (streamSettings.outputName && streamSettings.outputName.toLowerCase().includes('rtmp')) ||
        (sessionData.exports || []).some(exp => exp.isEnabled && getExportTypeName(exp.type).toLowerCase().includes('rtmp'));

    if (hasRTMP && (streamSettings.streamType === 'live' || streamSettings.streamType === 'average')) {
        suggestions.push({
            type: 'config',
            icon: 'video_settings',
            title: 'Stream Profile Advice',
            message: 'When streaming via RTMP to a CDN, "Live" or "Average" profiles have minimal impact on latency. Consider using "Best Quality" for improved visual performance.'
        });
    }

    // Signal Suggestion: Check for weak signals
    const weakConnections = deviceInfo.filter(d => d.signalStrength !== undefined && d.signalStrength >= 0 && d.signalStrength < 25);
    if (weakConnections.length > 0) {
        // Only show one general suggestion if multiple are weak to avoid cluttering
        const names = weakConnections.slice(0, 2).map(c => {
            const devName = c.deviceName || c.name || 'Unknown';
            const provName = c.operatorName || c.providerName;
            return provName ? `${devName} (${provName})` : devName;
        }).join(', ');
        suggestions.push({
            type: 'hardware',
            icon: 'signal_cellular_alt_1_bar',
            title: 'Weak Signal Detected',
            message: `Connections like ${names} reported poor signal strength during the session (below 25%). This can cause packet loss and bitrate fluctuations.`
        });
    }

    // Battery / Thermal Suggestion: Check for high temperature
    const hotDevices = deviceInfo.filter(d => d.temperature !== undefined && d.temperature > 45);
    if (hotDevices.length > 0) {
        const names = hotDevices.map(c => c.deviceName || c.name || 'Unknown').join(', ');
        suggestions.push({
            type: 'hardware',
            icon: 'thermostat',
            title: 'Thermal Warning',
            message: `High battery temperature detected on ${names} (${hotDevices[0].temperature}°C). Excessive heat can lead to performance throttling and connection drops.`
        });
    }

    // Bonding Suggestion: Check efficiency if bitrate is struggling
    const avgBitrateArr = bitrateSamples.filter(b => !isNaN(b));
    const avgBitrate = avgBitrateArr.length > 0 ? (avgBitrateArr.reduce((a, b) => a + b, 0) / avgBitrateArr.length) : 0;
    const targetBitrate = parseFloat(streamSettings.maxBitrate) || 0;

    if (targetBitrate > 0 && avgBitrate < (targetBitrate * 0.6) && deviceInfo.length > 1) {
        suggestions.push({
            type: 'performance',
            icon: 'lan',
            title: 'Bonding Efficiency',
            message: 'The actual bitrate was significantly lower than the target despite having multiple connections. Verify your bonding configuration and provider limits.'
        });
    }

    // 3. Bitrate Optimization Suggestion
    const BITRATE_GUIDE = {
        'sd': {
            'low': { h264: 2, h265: 1, message: 'H.264 is standard; H.265 saves data.' }
        },
        'hd': {
            'low': { h264: 4, h265: 2, message: 'this is stable for most social platforms.' },
            'high': { h264: 6, h265: 3, message: 'this is better for high-motion compatibility.' }
        },
        'full-hd': {
            'low': { h264: 6, h265: 3, message: 'this is the industry standard for high-quality video.' },
            'high': { h264: 8, h265: 5, message: 'use SRT to enable H.265 at 5Mb/s.' }
        },
        'ultra-hd': {
            'low': { h264: 16, h265: 8, message: 'SRT is required for H.265.' },
            'high': { h264: 16, h265: 12, message: 'SRT is required; H.264 is not recommended at 16Mb/s.' }
        }
    };

    const resLabels = {
        'sd': 'SD',
        'hd': 'HD',
        'full-hd': 'FULL HD',
        'ultra-hd': '4K'
    };

    const res = streamSettings.resolution || 'hd';
    const fps = parseInt(streamSettings.frameRate) || 30;
    const fpsCategory = fps > 30 ? 'high' : 'low';
    const currentMaxBitrate = parseFloat(streamSettings.maxBitrate) || 0;
    const codecValue = streamSettings.codec || 'H.264';
    const isH265 = codecValue === 'H.265';

    // Protocol check: H.265 requires SRT
    if (isH265 && !hasSRT) {
        suggestions.push({
            type: 'config',
            icon: 'warning',
            title: 'Protocol Mismatch',
            message: 'H.265 (HEVC) is only supported via the SRT protocol. Please switch your output to SRT or revert to H.264 codec.'
        });
    }

    const guide = BITRATE_GUIDE[res] && BITRATE_GUIDE[res][fpsCategory];
    if (guide) {
        const recommended = isH265 ? guide.h265 : guide.h264;

        // 4K 60fps specific warning: H.264 not recommended
        if (res === 'ultra-hd' && fpsCategory === 'high' && !isH265) {
            suggestions.push({
                type: 'optimization',
                icon: 'error_outline',
                title: 'Codec Recommendation',
                message: 'Streaming 4K at 60fps with H.264 is not recommended. Use SRT protocol and H.265 codec at 12Mb/s for optimal results.'
            });
        }
        // General bitrate optimization if significantly off-target
        else if (currentMaxBitrate > 0 && (currentMaxBitrate < recommended * 0.7 || currentMaxBitrate > recommended * 1.5)) {
            const status = currentMaxBitrate > recommended ? 'higher' : 'lower';
            const resLabel = resLabels[res] || res.toUpperCase();

            // Adjust message if it's the industry standard one and settings are higher
            let finalNote = guide.message;
            if (res === 'full-hd' && fpsCategory === 'low' && status === 'higher') {
                finalNote = 'this exceeds typical industry recommendations for high-quality video.';
            }

            suggestions.push({
                type: 'optimization',
                icon: 'auto_graph',
                title: 'Bitrate Optimization',
                message: `Your stream bitrate is ${currentMaxBitrate}Mb/s using the ${codecValue} codec, which is ${status} than the recommended ${recommended}Mb/s for ${resLabel} with ${codecValue}. ${finalNote}`
            });
        }
    }

    // 4. Cleanup Suggestion: Inactive exports
    const inactiveExports = (exports || []).filter(exp => !exp.isEnabled);
    if (inactiveExports.length > 0) {
        suggestions.push({
            type: 'config',
            icon: 'cleaning_services',
            title: 'Configuration Cleanup',
            message: `You have ${inactiveExports.length} inactive export${inactiveExports.length > 1 ? 's' : ''}. Consider deleting unused exports to simplify your setup.`
        });
    }

    return { summary, suggestions };
};

export const generateStreamReport = (sessionData, config) => {
    const {
        companyName,
        primaryName,
        startTime,
        endTime,
        bitrateSamples,
        targetBitrate,
        duration,
        exports,
        exportLabels,
        logs,
        alarms,
        streamSettings,
        accountInfo,
        licenseInfo,
        deviceInfo,
        connectivityEvents,
        maxSecondaries
    } = sessionData;

    const algoConfig = config.scoreAlgorithm || {};
    const reportFormat = config.format || 'csv';
    const enableScore = config.enableScore !== false; // Defaults to true if undefined


    // Mapping utility for broadcast settings
    const getDisplayedValue = (id, value) => {
        const setting = availableBroadcastSettings.find(s => s.id === id);
        if (setting && setting.valuesArray) {
            const option = setting.valuesArray.find(o => o.value === value);
            return option ? option.displayedName : value;
        }
        return value;
    };

    // Mapping utility for signal strength
    const getSignalLabel = (strength) => {
        if (strength === 99 || strength === undefined || strength === null) return 'N/A';
        const s = parseInt(strength);
        if (s >= 4) return 'Excellent';
        if (s === 3) return 'Good';
        if (s === 2) return 'Fair';
        if (s === 1) return 'Poor';
        return 'No Signal';
    };

    // Calculate metrics
    const avgBitrateArr = bitrateSamples.filter(b => !isNaN(b));
    const avgBitrate = avgBitrateArr.length > 0
        ? (avgBitrateArr.reduce((a, b) => a + b, 0) / avgBitrateArr.length).toFixed(2)
        : 0;

    const score = enableScore ? calculateStreamScore(sessionData, config) : 'N/A';

    const reportData = {
        info: {
            company: (accountInfo && accountInfo.company) || companyName || 'N/A',
            admin: (accountInfo && accountInfo.adminName) || 'N/A',
            email: (accountInfo && accountInfo.email) || 'N/A',
            phone: (accountInfo && accountInfo.phone) || 'N/A',
            primary: primaryName || 'N/A',
        },
        license: {
            registration: (licenseInfo && licenseInfo.registrationDate) || 'N/A',
            expiration: (licenseInfo && licenseInfo.expirationDate) || 'N/A',
            subscription: (licenseInfo && licenseInfo.subscription) || 'N/A',
            plan: (licenseInfo && licenseInfo.plan) || 'N/A',
            platform: (licenseInfo && licenseInfo.platform) || 'N/A',
        },
        device: (deviceInfo || []).map(device => ({
            name: device.deviceName || 'N/A',
            battery: device.batteryLevel ? `${device.batteryLevel}%` : 'N/A',
            temp: device.batteryTemperature ? `${device.batteryTemperature}°C` : 'N/A',
            provider: device.providerName || 'N/A',
            network: device.networkType || 'N/A',
            signal: getSignalLabel(device.signalStrength)
        })),
        summary: {
            camera: streamSettings ? `${getDisplayedValue('camera', streamSettings.camera)}` : 'N/A',
            scanMode: streamSettings ? `${getDisplayedValue('scanMode', streamSettings.scanMode)}` : 'N/A',
            resolution: streamSettings ? `${getDisplayedValue('resolution', streamSettings.resolution)}` : 'N/A',
            frameRate: streamSettings ? `${getDisplayedValue('frameRate', streamSettings.frameRate)}` : 'N/A',
            bitrate: targetBitrate ? `${getDisplayedValue('maxBitrate', streamSettings.maxBitrate)} Mb/s` : 'N/A',
            codec: streamSettings ? `${getDisplayedValue('codec', streamSettings.codec)}` : 'N/A',
            profile: streamSettings ? `${getDisplayedValue('streamType', streamSettings.streamType)}` : 'N/A',
            fluency: streamSettings ? `${getDisplayedValue('videoFluency', streamSettings.videoFluency)}` : 'N/A',
            broadcastType: streamSettings ? `${getDisplayedValue('broadcastType', streamSettings.broadcastType)}` : 'N/A',
            output: (streamSettings && streamSettings.outputName) || 'None',
        },
        metrics: {
            date: startTime ? new Date(startTime).toLocaleDateString() : 'N/A',
            startTime: startTime ? new Date(startTime).toLocaleTimeString() : 'N/A',
            endTime: endTime ? new Date(endTime).toLocaleTimeString() : 'N/A',
            duration: duration || '00:00:00',
            averageBitrate: `${avgBitrate || 0} Mb/s`,
            secondaryChannels: maxSecondaries || 0,
            streamScore: score || 'N/A',
        },
        exports: (exports || []).map(exp => {
            const labelObj = (exportLabels || []).find(l => parseInt(l.id) === parseInt(exp.id) || l.uuid === exp.uuid);
            const typeName = getExportTypeName(exp.type);



            return {
                exportId: exp.id,
                exportType: typeName,
                address: exp.url || exp.host || 'N/A',
                port: exp.port || 'N/A',
                streamID: exp.exportStreamId || 'N/A',
                passphrase: exp.key || 'N/A',
                label: labelObj ? labelObj.exportLabel : 'None',
                state: exp.isEnabled ? 'Active' : 'Inactive'
            };
        }),
        logs: (logs || []).map(log => ({
            time: log.time || 'N/A',
            message: log.message || ''
        })),
        alarms: (alarms || []).map(alarm => ({
            time: alarm.time || alarm.Time || 'N/A',
            name: alarm.Name || alarm.name || 'N/A',
            description: alarm.Description || alarm.message || 'No description'
        })),
        failures: (connectivityEvents || []).map(event => ({
            time: event.time,
            type: event.type,
            provider: event.provider,
            message: event.message
        })),
        insights: generateInsights(sessionData)
    };

    if (reportFormat === 'json') {
        downloadFile(
            JSON.stringify(reportData, null, 2),
            `stream_report_${Date.now()}.json`,
            'application/json'
        );
    } else {
        const csvContent = generateCSV(reportData);
        downloadFile(
            csvContent,
            `stream_report_${Date.now()}.csv`,
            'text/csv'
        );
    }
};

export const getExportTypeName = (type) => {
    switch (type) {
        case 0: return 'Custom RTMP/S';
        case 1: return 'SRT Caller';
        case 2: return 'SRT Listener';
        case 3: return 'Facebook Live';
        case 4: return 'Facebook Live';
        case 5: return 'Facebook Live';
        case 6: return 'YouTube Live';
        case 7: return 'Omni Player RTMP';
        case 8: return 'Omni Player SRT';
        case 9: return 'Twitch Live';
        case 10: return 'Telegram';
        case 11: return 'Instagram';
        case 12: return 'Kick Live';
        case 13: return 'Dacast RTMP';
        case 14: return 'Dacast SRT';
        default: return `Type ${type}` || 'N/A';
    }
};

const generateCSV = (data) => {
    const lines = [];

    // Account Section
    lines.push('--- ACCOUNT DETAILS ---');
    Object.keys(data.info).forEach(key => {
        const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        lines.push(`${displayKey},${data.info[key]}`);
    });

    lines.push('');

    // License Section
    lines.push('--- LICENSE INFO ---');
    Object.keys(data.license).forEach(key => {
        const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        lines.push(`${displayKey},${data.license[key]}`);
    });

    lines.push('');

    // Device Section
    lines.push('--- DEVICE INFO ---');
    lines.push('Device Name,Battery,Temperature,Provider,Network,Signal');
    data.device.forEach(dev => {
        lines.push(`"${dev.name}","${dev.battery}","${dev.temp}","${dev.provider}","${dev.network}","${dev.signal}"`);
    });

    lines.push('');

    // Stream Section
    lines.push('--- STREAM SETTINGS ---');
    Object.keys(data.summary).forEach(key => {
        const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        lines.push(`${displayKey},${data.summary[key]}`);
    });

    lines.push('');

    // Exports Section
    lines.push('--- EXPORT CONFIGURATIONS ---');
    lines.push(`Total Exports,${data.exports.length}`);
    lines.push('Export ID,Export Type,Address,Port,Stream ID,Passphrase,Label,State');
    data.exports.forEach(exp => {
        lines.push(`${exp.exportId},"${exp.exportType}","${exp.address}","${exp.port}","${exp.streamID}","${exp.passphrase}","${exp.label}",${exp.state}`);
    });

    lines.push('');

    // Alerts Section
    lines.push('--- ALERTS/ALARMS ---');
    lines.push('Time,Name,Description');
    data.alarms.forEach(alarm => {
        lines.push(`"${alarm.time}","${alarm.name}","${alarm.description}"`);
    });

    lines.push('');

    // Logs Section
    lines.push('--- SYSTEM LOGS ---');
    lines.push('Time,Message');
    data.logs.forEach(log => {
        lines.push(`"${log.time}","${log.message.replace(/"/g, '""')}"`);
    });

    lines.push('');

    // Metrics Section (Summary)
    lines.push('--- STREAM SUMMARY ---');
    Object.keys(data.metrics).forEach(key => {
        const displayKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        lines.push(`${displayKey},${data.metrics[key]}`);
    });

    lines.push('');

    // Failure Analysis Section
    lines.push('--- FAILURE ANALYSIS (Connectivity Events) ---');
    if (data.failures && data.failures.length > 0) {
        lines.push('Time,Event,Provider,Details');
        data.failures.forEach(f => {
            lines.push(`"${f.time}","${f.type}","${f.provider}","${f.message}"`);
        });
    } else {
        lines.push('No connectivity failures detected during this session.');
    }

    return lines.join('\n');
};

const downloadFile = (content, fileName, contentType) => {
    const a = document.createElement('a');
    const file = new Blob([content], { type: contentType });
    a.href = URL.createObjectURL(file);
    a.download = fileName;
    a.click();
};
